<template>
  <div class="flex flex-col items-start w-full min-w-[600px]">
    <!-- Delete Confirmation Modal -->
    <DeleteEncounterModal
      v-model="showDeleteModal"
      :note-id="noteToDelete?.id"
      @deleted="handleDeleted"
      @close="handleCloseDeleteModal"
    />

    <Repeater
      class="w-full"
      :columns="columns"
      :rows="notes"
      row_key="id"
      size="medium"
      horizontal_divider="dashed"
      :column_widths="['15%', '14%', '13%', '8%', '44%', '6%']"
    >
      <template #cell-date="{ row }">
        <span
          class="text-sm text-[#525252] tracking-[0.25px] leading-5 font-normal block cursor-pointer"
          style="font-family: 'Inter', sans-serif;"
          @click="handleRowClick(row)"
        >
          {{ formatEncounterDate(row.created_at) }}
        </span>
      </template>

      <template #cell-patient_name="{ row }">
        <span
          class="text-sm text-[#525252] tracking-[0.1px] leading-5 font-semibold block cursor-pointer"
          style="font-family: 'Inter', sans-serif; word-break: break-word;"
          @click="handleRowClick(row)"
        >
          {{ row.patient_name }}
        </span>
      </template>

      <template #cell-appointment_type="{ row }">
        <span
          class="text-sm text-[#525252] tracking-[0.25px] leading-5 font-normal block cursor-pointer"
          style="font-family: 'Inter', sans-serif;"
          @click="handleRowClick(row)"
        >
          {{ row.appointment_type }}
        </span>
      </template>

      <template #cell-status="{ row }">
        <div class="cursor-pointer" @click="handleRowClick(row)">
          <NoteStatusBadge :status="isDraft(row) ? 'draft' : 'signed'" />
        </div>
      </template>

      <template #cell-description="{ row }">
        <div
          class="overflow-hidden flex items-center gap-1 cursor-pointer w-full min-w-0"
          @click="handleRowClick(row)"
        >
          <img
            v-if="isAutoGenerated(row)"
            :src="autoGeneratedIcon"
            alt="Auto-generated"
            class="w-5 h-5 flex-shrink-0"
          />
          <span
            class="text-[16px] text-[#525252] tracking-[0.5px] leading-6 font-normal truncate flex-1 min-w-0 whitespace-nowrap"
            style="font-family: 'Inter', sans-serif;"
          >
            {{ getNotePreview(row) }}
          </span>
        </div>
      </template>

      <template #cell-actions="{ row }">
        <div class="flex items-center gap-1 action-icons">
          <!-- Edit icon (only for draft) -->
          <ResponsiveButton
            v-if="isDraft(row)"
            button_type="text"
            built_in_theme="dark"
            title="Edit"
            @click.stop="handleRowClick(row)"
          >
            <svg class="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0588 9.02L14.9788 9.94L5.91878 19H4.99878V18.08L14.0588 9.02ZM17.6588 3C17.4088 3 17.1488 3.1 16.9588 3.29L15.1288 5.12L18.8788 8.87L20.7088 7.04C21.0988 6.65 21.0988 6.02 20.7088 5.63L18.3688 3.29C18.1688 3.09 17.9188 3 17.6588 3ZM14.0588 6.19L2.99878 17.25V21H6.74878L17.8088 9.94L14.0588 6.19Z"  fill="currentColor"/>
            </svg>
          </ResponsiveButton>

          <!-- Delete icon (only for draft) -->
          <ResponsiveButton
            v-if="isDraft(row)"
            button_type="text"
            built_in_theme="dark"
            title="Delete"
            @click.stop="handleDeleteClick(row)"
          >
            <svg class="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 9V19H8V9H16ZM14.5 3H9.5L8.5 4H5V6H19V4H15.5L14.5 3ZM18 7H6V19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7Z"  fill="currentColor"/>
            </svg>
          </ResponsiveButton>

          <!-- Download icon (only for signed) -->
          <ResponsiveButton
            v-if="!isDraft(row)"
            button_type="text"
            built_in_theme="dark"
            title="Download"
            @click.stop="$emit('download', row)"
          >
            <svg class="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 15V18H6V15H4V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V15H18ZM17 11L15.59 9.59L13 12.17V4H11V12.17L8.41 9.59L7 11L12 16L17 11Z"  fill="currentColor"/>
            </svg>
          </ResponsiveButton>
        </div>
      </template>
    </Repeater>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import NoteStatusBadge from './NoteStatusBadge.vue';
import DeleteEncounterModal from './DeleteEncounterModal.vue';
import { formatEncounterDate } from '../utils/encounterNotes';
import { generateNotePreview } from '../utils/actionCardUtils';
import autoGeneratedIcon from '@/assets/encounterNotes/auto-generated icon.svg';

const router = useRouter();

const props = defineProps({
  notes: {
    type: Array,
    required: false,
    default: () => []
  },
  inlineTextMap: {
    type: Object,
    default: () => ({})
  }
});

const getNotePreview = (note) => {
  return generateNotePreview(note.content, note.content_text, note.title, props.inlineTextMap);
};

const emit = defineEmits(['edit', 'deleted', 'download']);

// Repeater column config — keys match the #cell-{key} slot names below.
// `actions` has no underlying data field; its content is rendered entirely
// via the slot. Widths are mirrored to :column_widths on the template.
const columns = [
  { key: 'date', label: 'Date', align: 'left' },
  { key: 'patient_name', label: 'Patient name', align: 'left' },
  { key: 'appointment_type', label: 'Appointment type', align: 'left' },
  { key: 'status', label: 'Status', align: 'left' },
  { key: 'description', label: 'Description', align: 'left' },
  { key: 'actions', label: 'Action', align: 'left' },
];

const isAutoGenerated = (note) => {
  return (note.title || '').startsWith('[Auto]');
};

// Check if note is a draft (not signed)
const isDraft = (note) => {
  return note.signed_at === null || note.signed_at === undefined;
};

// Navigate to note detail page
const handleRowClick = (note) => {
  router.push(`/encounter-notes/${note.patient_id}/${note.id}`);
};

// Delete modal state
const showDeleteModal = ref(false);
const noteToDelete = ref(null);

const handleDeleteClick = (note) => {
  noteToDelete.value = note;
  showDeleteModal.value = true;
};

const handleDeleted = () => {
  emit('deleted');
  noteToDelete.value = null;
};

const handleCloseDeleteModal = () => {
  noteToDelete.value = null;
};

</script>

<style scoped>
/* Hover styles for mic icon - change to teal color #20A7C1 */
.note-row:hover .mic-icon-wrapper svg path {
  fill: #20A7C1;
  stroke: #20A7C1;
}
</style>
